import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Document, Page, pdfjs } from 'react-pdf';
import writeXlsxFile from 'write-excel-file/browser';
import {
  AlertTriangle,
  BadgeCheck,
  CheckCircle2,
  Download,
  FileCode,
  FileSpreadsheet,
  FileText,
  Loader2,
  Play,
  Receipt,
  RotateCcw,
  ScanText,
  ShieldCheck,
  TableProperties,
  Tags,
  Upload,
} from 'lucide-react';
import { Container, SectionHeading, fadeInUp } from './shared';
import { trackEvent, AnalyticsEvents } from '../../utils/analytics';

// pdf.js worker — same CDN pattern as Dashboard, but .mjs (pdfjs-dist v5)
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const SAMPLE_PDF = '/sample-files/sample-invoice.pdf';
const MAX_DEMO_PAGES = 3;

// --- GL classification ---
// Keyword rules mirroring the GL chart in backend/dashboard_handlers.go.
// Order matters: the first matching rule wins.
const GL_RULES: Array<{ code: string; category: string; keywords: string[] }> = [
  { code: '6150', category: 'Telecommunications', keywords: ['internet', 'telecom', 'mobile', 'landline', 'phone'] },
  { code: '6160', category: 'Postage & Courier', keywords: ['courier', 'postage', 'shipping', 'delivery', 'parcel'] },
  { code: '6230', category: 'Hotels & Accommodation', keywords: ['hotel', 'accommodation', 'airbnb'] },
  { code: '6320', category: 'Air Travel', keywords: ['flight', 'airline', 'air ticket'] },
  { code: '6330', category: 'Ground Transport', keywords: ['taxi', 'train', 'rail', 'uber', 'bus ticket'] },
  { code: '6310', category: 'Fuel & Gasoline', keywords: ['fuel', 'diesel', 'petrol', 'gasoline'] },
  { code: '6350', category: 'Parking & Tolls', keywords: ['parking', 'toll'] },
  { code: '6340', category: 'Vehicle Maintenance', keywords: ['car service', 'tire', 'vehicle', 'inspection'] },
  { code: '6210', category: 'Food & Beverages', keywords: ['coffee', 'water', 'beverage', 'snack', 'food', 'refreshment'] },
  { code: '6220', category: 'Restaurant & Catering', keywords: ['restaurant', 'catering', 'dinner', 'lunch'] },
  { code: '6410', category: 'Electricity', keywords: ['electricity', 'electric'] },
  { code: '6420', category: 'Gas & Heating', keywords: ['heating', 'gas '] },
  { code: '6440', category: 'Waste Management', keywords: ['waste', 'recycling', 'rubbish'] },
  { code: '6820', category: 'Cleaning Services', keywords: ['cleaning', 'janitorial'] },
  { code: '6830', category: 'Security Services', keywords: ['security', 'cctv', 'alarm', 'guard'] },
  { code: '6810', category: 'Building Maintenance', keywords: ['hvac', 'building', 'repair', 'maintenance'] },
  { code: '6650', category: 'IT Consulting & Development', keywords: ['software development', 'it consulting', 'integration', 'api '] },
  { code: '6130', category: 'IT Equipment & Hardware', keywords: ['monitor', 'laptop', 'computer', 'printer', 'keyboard', 'server', 'hardware', 'lcd'] },
  { code: '6140', category: 'Software & Licenses', keywords: ['software', 'license', 'licence', 'subscription', 'saas', 'microsoft'] },
  { code: '6110', category: 'Office Supplies', keywords: ['paper', 'stationery', 'pen', 'binder', 'toner', 'ink cartridge'] },
  { code: '6120', category: 'Furniture & Fixtures', keywords: ['chair', 'desk', 'shelf', 'furniture', 'cabinet'] },
  { code: '6520', category: 'Printing & Publications', keywords: ['printing', 'business card', 'brochure', 'banner', 'catalogue'] },
  { code: '6510', category: 'Marketing & Advertising', keywords: ['marketing', 'advertis', 'campaign', 'sponsor'] },
  { code: '6530', category: 'Trade Shows & Events', keywords: ['exhibition', 'trade show', 'conference', 'booth'] },
  { code: '6610', category: 'Legal Services', keywords: ['legal', 'attorney', 'lawyer', 'notary'] },
  { code: '6630', category: 'Accounting & Audit', keywords: ['accounting', 'audit', 'bookkeep', 'tax return'] },
  { code: '6620', category: 'Consulting Services', keywords: ['consulting', 'consultancy', 'advisory'] },
  { code: '6640', category: 'HR & Recruitment', keywords: ['recruitment', 'staffing', 'headhunt'] },
  { code: '6710', category: 'Training & Education', keywords: ['training', 'course', 'workshop', 'seminar'] },
  { code: '6730', category: 'Books & Periodicals', keywords: ['book', 'journal', 'periodical', 'publication'] },
  { code: '6910', category: 'Insurance Premiums', keywords: ['insurance', 'policy premium'] },
  { code: '6920', category: 'Rent & Property Lease', keywords: ['rent', 'office space', 'property lease'] },
  { code: '6930', category: 'Bank Charges & Fees', keywords: ['bank charge', 'transaction fee', 'wire transfer'] },
  { code: '7110', category: 'Employee Benefits', keywords: ['benefit', 'gym', 'meal voucher'] },
  { code: '7120', category: 'Staff Uniforms & PPE', keywords: ['uniform', 'ppe', 'safety gear', 'workwear'] },
];

const classifyGL = (description: string): { code: string; category: string; confidence: number } => {
  const d = description.toLowerCase();
  for (const rule of GL_RULES) {
    if (rule.keywords.some((k) => d.includes(k))) {
      // Deterministic pseudo-confidence so the demo looks stable between runs
      const jitter = (description.length * 7) % 8;
      return { code: rule.code, category: rule.category, confidence: 0.9 + jitter / 100 };
    }
  }
  return { code: '9000', category: 'Other Expenses', confidence: 0.55 };
};

// --- PDF text extraction & parsing ---
interface TextCell {
  x: number;
  s: string;
}
type Row = TextCell[];

interface LineItem {
  description: string;
  qty: number;
  unitPrice: number;
  amount: number;
  glCode: string;
  glCategory: string;
  confidence: number;
}

interface InvoiceResult {
  invoiceNumber?: string;
  issueDate?: string;
  dueDate?: string;
  vendor?: string;
  currency?: string;
  items: LineItem[];
  netTotal?: number;
  vat?: number;
  grossTotal?: number;
  totalsMatch: boolean;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const parseNumber = (s: string): number | null => {
  const cleaned = s.replace(/[\s,]/g, '');
  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return null;
  return parseFloat(cleaned);
};

const isAmount = (s: string) => /^\d[\d\s,]*\.\d{2}$/.test(s.trim());
const isQty = (s: string) => /^\d+([.,]\d+)?$/.test(s.trim());

/** Extract text rows (grouped by y position, cells sorted by x) from a PDF. */
const extractRows = async (source: string | File): Promise<Row[]> => {
  const task =
    typeof source === 'string'
      ? pdfjs.getDocument(source)
      : pdfjs.getDocument({ data: new Uint8Array(await source.arrayBuffer()) });
  const doc = await task.promise;
  const allRows: Row[] = [];

  const pages = Math.min(doc.numPages, MAX_DEMO_PAGES);
  for (let p = 1; p <= pages; p++) {
    const page = await doc.getPage(p);
    const content = await page.getTextContent();
    const rows = new Map<number, TextCell[]>();
    for (const item of content.items as any[]) {
      if (!item.str || !item.str.trim()) continue;
      const x = item.transform[4] as number;
      const y = item.transform[5] as number;
      let key: number | undefined = Array.from(rows.keys()).find((k) => Math.abs(k - y) < 3);
      if (key === undefined) {
        key = y;
        rows.set(key, []);
      }
      const bucket = rows.get(key);
      if (bucket) bucket.push({ x, s: item.str.trim() });
    }
    const sorted = Array.from(rows.entries())
      .sort((a, b) => b[0] - a[0])
      .map(([, cells]) => cells.sort((a, b) => a.x - b.x));
    allRows.push(...sorted);
  }
  return allRows;
};

/** Parse extracted text rows into a structured invoice result. */
const parseInvoice = (rows: Row[]): InvoiceResult => {
  const lines = rows.map((r) => r.map((c) => c.s).join(' '));
  const result: InvoiceResult = { items: [], totalsMatch: false };

  const num = (s: string | undefined) => (s ? parseNumber(s.replace(/\s/g, '')) : null);

  // --- line items: a row whose last 3 cells are qty / unit / amount ---
  for (const row of rows) {
    let cells = row.map((c) => c.s);
    // Fallback: a single merged text run -> split on wide gaps
    if (cells.length === 1) cells = cells[0].split(/\s{2,}/);
    if (cells.length < 4) continue;

    const amount = num(cells[cells.length - 1]);
    const unit = num(cells[cells.length - 2]);
    const qty = num(cells[cells.length - 3]);
    if (amount === null || unit === null || qty === null) continue;
    if (!isAmount(cells[cells.length - 1]) || !isAmount(cells[cells.length - 2])) continue;
    if (!isQty(cells[cells.length - 3])) continue;

    let descCells = cells.slice(0, -3);
    // Drop leading row-number cell
    if (descCells.length > 1 && /^\d{1,3}$/.test(descCells[0])) descCells = descCells.slice(1);
    const description = descCells.join(' ').trim();
    if (!description || description.length < 3 || /^[\d\s.,-]+$/.test(description)) continue;
    if (Math.abs(qty * unit - amount) > 0.05) continue; // sanity check

    const gl = classifyGL(description);
    result.items.push({ description, qty, unitPrice: unit, amount, glCode: gl.code, glCategory: gl.category, confidence: gl.confidence });
  }

  // --- header fields from joined lines ---
  const joined = lines.join('\n');
  const noMatch = joined.match(/invoice\s*(?:number|no|#)?\s*[:]\s*([A-Z0-9][A-Z0-9/\\-]{2,})/i);
  if (noMatch) result.invoiceNumber = noMatch[1];
  const issueMatch = joined.match(/issue date\s*[:]\s*(\d{4}[-/.]\d{1,2}[-/.]\d{1,2})/i);
  if (issueMatch) result.issueDate = issueMatch[1];
  const dueMatch = joined.match(/due date\s*[:]\s*(\d{4}[-/.]\d{1,2}[-/.]\d{1,2})/i);
  if (dueMatch) result.dueDate = dueMatch[1];
  const curMatch = joined.match(/\b(PLN|EUR|USD|GBP|CHF)\b/);
  if (curMatch) result.currency = curMatch[1];

  // Vendor: first cell of the row following a "Seller:" cell
  const sellerIdx = rows.findIndex((r) => r.some((c) => /^seller:?$/i.test(c.s)));
  if (sellerIdx >= 0 && sellerIdx + 1 < rows.length) {
    const vendorCell = rows[sellerIdx + 1].find((c) => c.x < 300);
    if (vendorCell) result.vendor = vendorCell.s;
  }
  if (!result.vendor) {
    const m = joined.match(/([A-Z][^\n]{2,60}\b(?:sp\.?\s*z\s*o\.?o\.?|ltd|inc|gmbh|llc|s\.a\.)\b[^\n]*)/i);
    if (m) result.vendor = m[1].trim();
  }

  const pickTotal = (re: RegExp) => {
    const m = joined.match(re);
    return m ? parseNumber(m[1].replace(/\s/g, '')) : null;
  };
  result.netTotal = pickTotal(/net total\s*:?\s*([\d\s,]+\.\d{2})/i) ?? undefined;
  result.vat = pickTotal(/vat\s*\d*%\s*:?\s*([\d\s,]+\.\d{2})/i) ?? undefined;
  result.grossTotal = pickTotal(/gross total\s*:?\s*([\d\s,]+\.\d{2})/i) ?? undefined;

  if (result.netTotal != null && result.items.length) {
    const sum = result.items.reduce((a, i) => a + i.amount, 0);
    result.totalsMatch = Math.abs(sum - result.netTotal) < 0.05;
  }
  return result;
};

// --- Export helpers ---
const xmlEsc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const buildInvoiceXml = (r: InvoiceResult): string => {
  const meta = [
    r.invoiceNumber && `  <InvoiceNumber>${xmlEsc(r.invoiceNumber)}</InvoiceNumber>`,
    r.issueDate && `  <IssueDate>${r.issueDate}</IssueDate>`,
    r.dueDate && `  <DueDate>${r.dueDate}</DueDate>`,
    r.currency && `  <Currency>${r.currency}</Currency>`,
    r.vendor && `  <Vendor>${xmlEsc(r.vendor)}</Vendor>`,
    r.netTotal != null && `  <NetTotal>${r.netTotal.toFixed(2)}</NetTotal>`,
    r.vat != null && `  <VatTotal>${r.vat.toFixed(2)}</VatTotal>`,
    r.grossTotal != null && `  <GrossTotal>${r.grossTotal.toFixed(2)}</GrossTotal>`,
  ]
    .filter(Boolean)
    .join('\n');
  const items = r.items
    .map(
      (it, i) => `    <Item>
      <No>${i + 1}</No>
      <Description>${xmlEsc(it.description)}</Description>
      <Quantity>${it.qty}</Quantity>
      <UnitPrice>${it.unitPrice.toFixed(2)}</UnitPrice>
      <NetAmount>${it.amount.toFixed(2)}</NetAmount>
      <GLCode>${it.glCode}</GLCode>
      <GLCategory>${xmlEsc(it.glCategory)}</GLCategory>
      <Confidence>${it.confidence}</Confidence>
    </Item>`
    )
    .join('\n');
  return `<?xml version="1.0" encoding="UTF-8"?>\n<Invoice>\n${meta}\n  <Items>\n${items}\n  </Items>\n</Invoice>\n`;
};

const downloadBlob = (content: BlobPart, type: string, filename: string) => {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const exportBaseName = (r: InvoiceResult) =>
  `invoice-${(r.invoiceNumber || 'data').replace(/[^A-Za-z0-9_-]+/g, '-')}`;

// --- Component ---
type Stage = 'idle' | 'processing' | 'done' | 'error';

const STEP_ICONS = [FileText, ScanText, TableProperties, Tags];

const InvoiceDemo: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [pdfSource, setPdfSource] = useState<string | File>(SAMPLE_PDF);
  const [fileName, setFileName] = useState('sample-invoice.pdf');
  const [stage, setStage] = useState<Stage>('idle');
  const [stepIndex, setStepIndex] = useState(-1);
  const [result, setResult] = useState<InvoiceResult | null>(null);
  const [visibleItems, setVisibleItems] = useState(0);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const revealTimer = useRef<number | null>(null);

  const steps = t('landing.invoiceDemo.steps', { returnObjects: true }) as string[];

  const fmtMoney = useCallback(
    (v: number, currency?: string) =>
      `${new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v)} ${currency || ''}`.trim(),
    [i18n.language]
  );

  const reset = () => {
    if (revealTimer.current) window.clearInterval(revealTimer.current);
    setStage('idle');
    setStepIndex(-1);
    setResult(null);
    setVisibleItems(0);
    setError('');
  };

  const handleFilePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    reset();
    // Check the %PDF magic bytes, not just the extension — catches files
    // renamed to .pdf (e.g. an XML invoice) that pdf.js cannot parse.
    const header = new Uint8Array(await file.slice(0, 5).arrayBuffer());
    const isPdf = String.fromCharCode.apply(null, Array.from(header)) === '%PDF-';
    if (file.type !== 'application/pdf' || !isPdf) {
      setError(t('landing.invoiceDemo.errors.invalidPdf'));
      return;
    }
    setPdfSource(file);
    setFileName(file.name);
  };

  const useSample = () => {
    reset();
    setPdfSource(SAMPLE_PDF);
    setFileName('sample-invoice.pdf');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const runDemo = async () => {
    if (stage === 'processing') return;
    trackEvent(AnalyticsEvents.CTA_CLICK, { location: 'invoice_demo', target: 'process_invoice' });
    reset();
    setStage('processing');
    setError('');

    try {
      // Step 1: open document
      setStepIndex(0);
      const rowsPromise = extractRows(pdfSource);
      await sleep(500);

      // Step 2: OCR / text extraction
      setStepIndex(1);
      const rows = await rowsPromise;
      if (!rows.length) throw new Error('no-text');
      await sleep(800);

      // Step 3: line-item detection
      setStepIndex(2);
      const parsed = parseInvoice(rows);
      await sleep(700);

      // Step 4: GL classification
      setStepIndex(3);
      await sleep(600);

      setResult(parsed);
      setStage('done');

      // Reveal categorized rows one by one
      let i = 0;
      revealTimer.current = window.setInterval(() => {
        i += 1;
        setVisibleItems(i);

        if (i >= parsed.items.length && revealTimer.current) {
          window.clearInterval(revealTimer.current);
          revealTimer.current = null;
        }
      }, 220);
    } catch (e: any) {
      console.error('[InvoiceDemo] processing failed:', e);
      setStage('error');
      const name = e?.name || '';
      const msg =
        e?.message === 'no-text'
          ? t('landing.invoiceDemo.errors.noText')
          : name === 'PasswordException'
            ? t('landing.invoiceDemo.errors.password')
            : name === 'InvalidPDFException'
              ? t('landing.invoiceDemo.errors.invalidPdf')
              : t('landing.invoiceDemo.errors.generic');
      setError(msg);
    }
  };

  const downloadXml = () => {
    if (!result) return;
    trackEvent(AnalyticsEvents.CTA_CLICK, { location: 'invoice_demo', target: 'download_xml' });
    downloadBlob(buildInvoiceXml(result), 'application/xml', `${exportBaseName(result)}.xml`);
  };

  const downloadExcel = async () => {
    if (!result) return;
    trackEvent(AnalyticsEvents.CTA_CLICK, { location: 'invoice_demo', target: 'download_excel' });
    const label = (v: string) => ({ value: v, fontWeight: 'bold' as const });
    const money = (v: number) => ({ value: v, type: Number, format: '#,##0.00' });
    const blank = { value: '' };
    const rows: any[][] = [
      [label(t('landing.invoiceDemo.fields.vendor')), { value: result.vendor || '' }],
      [label(t('landing.invoiceDemo.fields.invoiceNo')), { value: result.invoiceNumber || '' }],
      [label(t('landing.invoiceDemo.fields.date')), { value: result.issueDate || '' }],
      [label(t('landing.invoiceDemo.fields.dueDate')), { value: result.dueDate || '' }],
      [label(t('landing.invoiceDemo.fields.currency')), { value: result.currency || '' }],
      [],
      [
        label('#'),
        label(t('landing.invoiceDemo.table.description')),
        label(t('landing.invoiceDemo.table.qty')),
        label(t('landing.invoiceDemo.table.unitPrice')),
        label(t('landing.invoiceDemo.table.amount')),
        label(t('landing.invoiceDemo.table.gl')),
        label(t('landing.invoiceDemo.table.glCategory')),
        label(t('landing.invoiceDemo.table.confidence')),
      ],
      ...result.items.map((it, i) => [
        { value: i + 1, type: Number },
        { value: it.description },
        { value: it.qty, type: Number },
        money(it.unitPrice),
        money(it.amount),
        { value: it.glCode },
        { value: it.glCategory },
        { value: Math.round(it.confidence * 100), type: Number, format: '0"%"' },
      ]),
      [],
    ];
    if (result.netTotal != null) rows.push([label(t('landing.invoiceDemo.totals.net')), blank, blank, blank, money(result.netTotal)]);
    if (result.vat != null) rows.push([label(t('landing.invoiceDemo.totals.vat')), blank, blank, blank, money(result.vat)]);
    if (result.grossTotal != null) rows.push([label(t('landing.invoiceDemo.totals.gross')), blank, blank, blank, money(result.grossTotal)]);

    await writeXlsxFile(rows, {
      columns: [{ width: 5 }, { width: 48 }, { width: 8 }, { width: 13 }, { width: 13 }, { width: 10 }, { width: 28 }, { width: 12 }],
      sheet: 'Invoice',
    }).toFile(`${exportBaseName(result)}.xlsx`);
  };

  const shownItems = result?.items.slice(0, visibleItems) ?? [];

  return (
    <section id="invoice-demo" className="relative py-24 sm:py-32">
      {/* ambient glow */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-[-10%] top-[20%] h-[400px] w-[500px] rounded-full bg-brand-primary/10 blur-[140px]" />
      </div>

      <Container className="relative">
        <SectionHeading
          eyebrow={t('landing.invoiceDemo.eyebrow')}
          title={t('landing.invoiceDemo.title')}
          subtitle={t('landing.invoiceDemo.subtitle')}
        />

        <div className="mt-16 grid gap-8 lg:grid-cols-2">
          {/* Left: invoice preview */}
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="glass-card flex flex-col rounded-2xl p-6"
          >
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-brand-button">
                  <Receipt className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-white">{fileName}</p>
                  <p className="text-xs text-brand-muted">{t('landing.invoiceDemo.previewCaption')}</p>
                </div>
              </div>
              {stage === 'done' && (
                <span className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs font-semibold text-emerald-300">
                  <BadgeCheck className="h-3.5 w-3.5" />
                  {t('landing.invoiceDemo.processedBadge')}
                </span>
              )}
            </div>

            <div className="relative flex-1 overflow-hidden rounded-xl border border-white/[0.08] bg-white">
              <Document
                file={pdfSource}
                loading={
                  <div className="flex h-96 items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-brand-primary" />
                  </div>
                }
                error={
                  <div className="flex h-96 items-center justify-center text-sm text-red-500">
                    {t('landing.invoiceDemo.errors.preview')}
                  </div>
                }
              >
                <Page
                  pageNumber={1}
                  width={520}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Document>

              {/* scan-line overlay while processing */}
              <AnimatePresence>
                {stage === 'processing' && (
                  <motion.div
                    initial={{ top: '0%' }}
                    animate={{ top: ['0%', '92%', '0%'] }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                    className="pointer-events-none absolute inset-x-0 h-1 bg-gradient-to-r from-transparent via-brand-secondary to-transparent shadow-glow-cyan"
                  />
                )}
              </AnimatePresence>
            </div>

            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <button
                onClick={runDemo}
                disabled={stage === 'processing'}
                className="group inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-brand-button px-6 py-3 text-sm font-semibold text-white shadow-glow transition-transform duration-300 hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {stage === 'processing' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : stage === 'done' ? (
                  <RotateCcw className="h-4 w-4" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
                {stage === 'done'
                  ? t('landing.invoiceDemo.runAgain')
                  : stage === 'processing'
                    ? t('landing.invoiceDemo.processing')
                    : t('landing.invoiceDemo.runDemo')}
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.04] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/[0.08]"
              >
                <Upload className="h-4 w-4" />
                {t('landing.invoiceDemo.uploadOwn')}
              </button>
              <input ref={fileInputRef} type="file" accept=".pdf" onChange={handleFilePick} className="sr-only" />
            </div>

            <div className="mt-3 flex items-center justify-between text-xs text-brand-muted">
              <button onClick={useSample} className="underline-offset-2 transition-colors hover:text-white hover:underline">
                {t('landing.invoiceDemo.useSample')}
              </button>
              <a
                href={SAMPLE_PDF}
                download
                className="inline-flex items-center gap-1.5 transition-colors hover:text-white"
              >
                <Download className="h-3.5 w-3.5" />
                {t('landing.invoiceDemo.downloadSample')}
              </a>
            </div>
          </motion.div>

          {/* Right: pipeline + results */}
          <motion.div
            variants={fadeInUp}
            custom={1}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="glass-card rounded-2xl p-6"
          >
            {/* pipeline steps */}
            <ol className="space-y-3">
              {steps.map((label, i) => {
                const Icon = STEP_ICONS[i];
                const state =
                  stage === 'done' || i < stepIndex
                    ? 'done'
                    : stage !== 'idle' && i === stepIndex
                      ? 'active'
                      : 'pending';
                return (
                  <li key={label} className="flex items-center gap-3">
                    <div
                      className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border transition-colors ${
                        state === 'done'
                          ? 'border-emerald-400/40 bg-emerald-400/10 text-emerald-300'
                          : state === 'active'
                            ? 'border-brand-secondary/50 bg-brand-secondary/10 text-brand-secondary'
                            : 'border-white/[0.08] bg-white/[0.03] text-brand-muted'
                      }`}
                    >
                      {state === 'done' ? (
                        <CheckCircle2 className="h-4 w-4" />
                      ) : state === 'active' ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Icon className="h-4 w-4" />
                      )}
                    </div>
                    <span className={`text-sm font-medium ${state === 'pending' ? 'text-brand-muted' : 'text-white'}`}>
                      {label}
                    </span>
                  </li>
                );
              })}
            </ol>

            {error && (
              <div className="mt-5 flex items-start gap-2 rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-300">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* results */}
            {result && (
              <div className="mt-6">
                {/* meta */}
                <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                  {result.invoiceNumber && (
                    <div>
                      <p className="text-xs uppercase tracking-wide text-brand-muted">{t('landing.invoiceDemo.fields.invoiceNo')}</p>
                      <p className="mt-0.5 font-semibold text-white">{result.invoiceNumber}</p>
                    </div>
                  )}
                  {result.issueDate && (
                    <div>
                      <p className="text-xs uppercase tracking-wide text-brand-muted">{t('landing.invoiceDemo.fields.date')}</p>
                      <p className="mt-0.5 font-semibold text-white">{result.issueDate}</p>
                    </div>
                  )}
                  {result.vendor && (
                    <div className="col-span-2">
                      <p className="text-xs uppercase tracking-wide text-brand-muted">{t('landing.invoiceDemo.fields.vendor')}</p>
                      <p className="mt-0.5 truncate font-semibold text-white">{result.vendor}</p>
                    </div>
                  )}
                </div>

                {/* items table */}
                {result.items.length > 0 ? (
                  <>
                    <div className="mt-5 overflow-hidden rounded-xl border border-white/[0.08]">
                      <table className="w-full text-left text-sm">
                        <thead>
                          <tr className="border-b border-white/[0.08] bg-white/[0.03] text-xs uppercase tracking-wide text-brand-muted">
                            <th className="px-3 py-2.5 font-medium">{t('landing.invoiceDemo.table.gl')}</th>
                            <th className="px-3 py-2.5 font-medium">{t('landing.invoiceDemo.table.description')}</th>
                            <th className="px-3 py-2.5 text-right font-medium">{t('landing.invoiceDemo.table.qty')}</th>
                            <th className="px-3 py-2.5 text-right font-medium">{t('landing.invoiceDemo.table.amount')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <AnimatePresence>
                            {shownItems.map((item, idx) => (
                              <motion.tr
                                key={`${item.description}-${idx}`}
                                initial={{ opacity: 0, x: 16 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.35 }}
                                className="border-b border-white/[0.05] last:border-0"
                              >
                                <td className="px-3 py-2.5">
                                  <span className="inline-flex items-center rounded-md border border-brand-secondary/30 bg-brand-secondary/10 px-2 py-0.5 font-mono text-xs font-semibold text-brand-secondary">
                                    {item.glCode}
                                  </span>
                                  <span className="mt-1 block text-[11px] text-brand-muted">{item.glCategory}</span>
                                </td>
                                <td className="px-3 py-2.5 text-white/90">{item.description}</td>
                                <td className="px-3 py-2.5 text-right text-brand-muted">{item.qty}</td>
                                <td className="px-3 py-2.5 text-right font-medium text-white">
                                  {fmtMoney(item.amount, result.currency)}
                                </td>
                              </motion.tr>
                            ))}
                          </AnimatePresence>
                        </tbody>
                      </table>
                    </div>

                    {/* totals */}
                    <div className="mt-4 space-y-1.5 text-sm">
                      {result.netTotal != null && (
                        <div className="flex justify-between text-brand-muted">
                          <span>{t('landing.invoiceDemo.totals.net')}</span>
                          <span>{fmtMoney(result.netTotal, result.currency)}</span>
                        </div>
                      )}
                      {result.vat != null && (
                        <div className="flex justify-between text-brand-muted">
                          <span>{t('landing.invoiceDemo.totals.vat')}</span>
                          <span>{fmtMoney(result.vat, result.currency)}</span>
                        </div>
                      )}
                      {result.grossTotal != null && (
                        <div className="flex justify-between border-t border-white/[0.08] pt-2 text-base font-semibold text-white">
                          <span>{t('landing.invoiceDemo.totals.gross')}</span>
                          <span>{fmtMoney(result.grossTotal, result.currency)}</span>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-3 text-xs">
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 font-semibold text-emerald-300">
                        <BadgeCheck className="h-3.5 w-3.5" />
                        {t('landing.invoiceDemo.itemsCategorized', { count: result.items.length })}
                      </span>
                      {result.totalsMatch && (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.1] bg-white/[0.04] px-3 py-1 font-semibold text-white/80">
                          <ShieldCheck className="h-3.5 w-3.5 text-brand-secondary" />
                          {t('landing.invoiceDemo.totalsVerified')}
                        </span>
                      )}
                    </div>

                    {/* export */}
                    <div className="mt-4 flex flex-wrap gap-3">
                      <button
                        onClick={downloadXml}
                        className="inline-flex items-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.04] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-white/[0.08]"
                      >
                        <FileCode className="h-3.5 w-3.5" />
                        {t('landing.invoiceDemo.downloadXml')}
                      </button>
                      <button
                        onClick={downloadExcel}
                        className="inline-flex items-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.04] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-white/[0.08]"
                      >
                        <FileSpreadsheet className="h-3.5 w-3.5" />
                        {t('landing.invoiceDemo.downloadExcel')}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="mt-5 flex items-start gap-2 rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-sm text-amber-200">
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    {t('landing.invoiceDemo.noItems')}
                  </div>
                )}
              </div>
            )}

            {stage === 'idle' && !result && (
              <div className="mt-6 rounded-xl border border-dashed border-white/[0.12] bg-white/[0.02] px-4 py-8 text-center text-sm text-brand-muted">
                {t('landing.invoiceDemo.idleHint')}
              </div>
            )}
          </motion.div>
        </div>

        <motion.p
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-8 text-center text-xs text-brand-muted"
        >
          {t('landing.invoiceDemo.privacyNote')}
        </motion.p>
      </Container>
    </section>
  );
};

export default InvoiceDemo;
